import { createApp } from "vue";
import App from "./App.vue";
import router from "./router/staff"; // Staff-specific router
import store from "./store";
import axios from "axios";
import logger, { createLogger } from "./utils/logger";
import urlHelper from "./utils/URLHelper";

// Bootstrap CSS and Icons
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

// Bootstrap JS
import { Tooltip, Toast, Popover } from "bootstrap";
import * as bootstrap from "bootstrap";

// Animate.css and scroll animation
import "animate.css";
import scrollanimation from "./v-scrollanimation";

// Vue Select component
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

// SweetAlert2
import Swal from "sweetalert2";

// Custom CSS
import "./assets/css/style.css";

// Configure axios for cookie-based authentication
axios.defaults.withCredentials = true;
axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL;

const app = createApp(App);

// Global properties
app.config.globalProperties.$swal = Swal;
app.config.globalProperties.$tooltip = Tooltip;
app.config.globalProperties.$toast = Toast;
app.config.globalProperties.$popover = Popover;
app.config.globalProperties.$bootstrap = bootstrap;

// Add logger as a global property
app.config.globalProperties.$logger = logger;
app.config.globalProperties.$createLogger = createLogger;

// Add URL helper for cross-subdomain navigation
app.config.globalProperties.$urlHelper = urlHelper;

// Configure for staff module
app.config.globalProperties.$module = "staff";

// Setup Axios interceptor for token refresh on 401
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip interception for refresh requests (prevents infinite loop)
    // Skip if already retried
    if (
      !originalRequest ||
      originalRequest._retry ||
      originalRequest.url?.includes("/refresh")
    ) {
      return Promise.reject(error);
    }

    // On 401, try to refresh the access token
    if (error.response?.status === 401) {
      // Only attempt refresh if staff is authenticated (has valid session)
      if (store.getters.isStaffAuthenticated) {
        originalRequest._retry = true;

        try {
          await store.dispatch("refreshAccessToken");
          const newAccessToken = store.state.accessToken;

          if (newAccessToken) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return axios(originalRequest);
          }
        } catch (refreshError) {
          logger.error("Token refresh failed, logging out");
          await store.dispatch("staffLogout");
          router.push({ name: "staffLogin" });
          return Promise.reject(refreshError);
        }
      } else {
        // Staff is not authenticated - silently reject (likely a background request after logout)
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// Setup idle timeout (15 minutes for staff - stricter than students)
let idleTimer = null;
const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes

const resetIdleTimer = () => {
  if (idleTimer) clearTimeout(idleTimer);

  // Only set timeout if staff is authenticated
  if (store.getters.isStaffAuthenticated) {
    idleTimer = setTimeout(() => {
      logger.warn("Staff idle timeout - logging out");
      store.dispatch("staffLogout");
      router.push({ name: "staffLogin" });
      Swal.fire({
        title: "Session Expired",
        text: "Your session has expired due to inactivity. Please log in again.",
        icon: "info",
      });
    }, IDLE_TIMEOUT);
  }
};

// Track user activity
const events = ["mousedown", "keydown", "scroll", "touchstart"];
events.forEach((event) => {
  document.addEventListener(event, resetIdleTimer, true);
});

// Reset idle timer when staff navigates
router.beforeEach((to, from, next) => {
  if (store.getters.isStaffAuthenticated) {
    resetIdleTimer();
  }
  next();
});

app
  .use(store)
  .use(router)
  .directive("scrollanimation", scrollanimation)
  .component("v-select", vSelect)
  .mount("#app");
